import { GridColDef } from '@mui/x-data-grid-premium';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ButtonCellComponent,
  EmployeeCellComponent,
  dateCellValueFormatter,
  getSingleSelectColumnEnumValueOptions,
} from '~/components/SolaForceDataGrid';
import { useAllEmployees } from '~/data/employees';
import { EnumListKey, useAllEnums } from '~/data/enums';
import { FormerAndNewEmployeesReportData } from '~/data/reports';
import { useTrialPeriodOptions } from '~/data/trialPeriod/useTrialPeriodOptions';
import {
  useIsEndedEmployeeViewRestricted,
  useReportNavigateToView,
} from '~/scenes/PortalModules/Analytics/common';
import { getEnumName } from '~/utils/helper';
import { convertToDateOrUndefined } from '~/utils/helpers/date';

export type FormerAndNewEmployeesReportRow = {
  employeeId: number;
  firstName: string;
  lastName: string;
  employeeNumber: string;
  unitId: number;
  unitName: string;
  unitNumber: string;
  managerName: string;
  managerNumber: string;
  position: string;
  jobTitle: string;
  legalEntityName: string;
  costCenterName: string;
  startDate?: Date;
  arrivalDate?: Date;
  endDate?: Date;
  terminationDate?: Date;
  terminationReason: string;
  trialPeriod: string;
  hasAccount: boolean;
};

type PropsType = {
  data: FormerAndNewEmployeesReportData[];
};

export const useFormerAndNewEmployeesReport = (props: PropsType) => {
  const { data } = props;

  const { t } = useTranslation();

  const { goToEmployeeBasicDataView } = useReportNavigateToView();
  const { isAccessToEndedEmployeeRestrictedByEmploymentStatus } =
    useIsEndedEmployeeViewRestricted();

  const { getEmployeeById } = useAllEmployees();
  const { getEnums } = useAllEnums();

  const { getTrialPeriodOptionName } = useTrialPeriodOptions();

  const enums = useMemo(() => {
    return {
      positionEnums: getEnums(EnumListKey.EMPLOYEE_POSITION),
      terminationReasonEnums: getEnums(EnumListKey.EMPL_TERMINATION_REASON),
    };
  }, [getEnums]);

  const rows = useMemo(() => {
    const { terminationReasonEnums, positionEnums } = enums;

    return data.map(
      (
        dataEntry: FormerAndNewEmployeesReportData,
      ): FormerAndNewEmployeesReportRow => {
        const {
          fEmpId,
          fEmpFirstName,
          fEmpLastName,
          fEmpEmployeeNumber,
          fEmpPosition,
          fEmpJobTitle,
          fEmpUnitId,
          fEmpUnit,
          fEmpUnitNumber,
          fEmpManagerId,
          fEmpLegalEntityName,
          fEmpCostCenterName,
          fEmpStartDate,
          fEmpArrivalDate,
          fEmpEndDate,
          fEmpTerminationDate,
          fEmpTerminationReason,
          fEmpTrialPeriod,
          fEmpHasAccount,
        } = dataEntry;

        const manager = getEmployeeById(fEmpManagerId);

        return {
          employeeId: fEmpId,
          firstName: fEmpFirstName,
          lastName: fEmpLastName,
          employeeNumber: fEmpEmployeeNumber,
          unitId: fEmpUnitId,
          unitName: fEmpUnit,
          unitNumber: fEmpUnitNumber,
          managerName: manager
            ? `${manager.fEmpFirstName} ${manager.fEmpLastName}`
            : '',
          managerNumber: manager?.fEmpPersonNumber ?? '',
          position: getEnumName(positionEnums, fEmpPosition),
          jobTitle: fEmpJobTitle,
          legalEntityName: fEmpLegalEntityName,
          costCenterName: fEmpCostCenterName,
          startDate: convertToDateOrUndefined(fEmpStartDate),
          arrivalDate: convertToDateOrUndefined(fEmpArrivalDate),
          endDate: convertToDateOrUndefined(fEmpEndDate),
          terminationDate: convertToDateOrUndefined(fEmpTerminationDate),
          terminationReason: getEnumName(
            terminationReasonEnums,
            fEmpTerminationReason,
          ),
          trialPeriod: getTrialPeriodOptionName(fEmpTrialPeriod) ?? '',
          hasAccount: fEmpHasAccount,
        };
      },
    );
  }, [data, enums, getEmployeeById, getTrialPeriodOptionName]);

  const dataColumnOptions = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        const includeIfMissing = (options: string[], value: string) => {
          if (value && !options.includes(value)) {
            options.push(value);
          }
        };

        includeIfMissing(acc.positionOptions, row.position);
        includeIfMissing(acc.terminationReasonOptions, row.terminationReason);

        return acc;
      },
      {
        positionOptions: [],
        terminationReasonOptions: [],
      } as {
        positionOptions: string[];
        terminationReasonOptions: string[];
      },
    );
  }, [rows]);

  const columns = useMemo((): GridColDef<FormerAndNewEmployeesReportRow>[] => {
    const { positionOptions, terminationReasonOptions } = dataColumnOptions;
    const { positionEnums, terminationReasonEnums } = enums;

    const isEndedEmployee = (row: FormerAndNewEmployeesReportRow) =>
      isAccessToEndedEmployeeRestrictedByEmploymentStatus({
        endDate: row.endDate,
        hasEndedStatus: !!row.endDate,
      });

    return [
      {
        field: 'employeeId',
        headerName: t('laPicture'),
        filterable: false,
        sortable: false,
        disableExport: true,
        renderCell: (params) => {
          return (
            <EmployeeCellComponent
              {...params}
              pictureOnly={true}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
              disabled={isEndedEmployee(params.row)}
            />
          );
        },
        minWidth: 30,
      },
      {
        field: 'firstName',
        headerName: t('laFirstName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
              disabled={isEndedEmployee(params.row)}
            />
          );
        },
        minWidth: 90,
      },
      {
        field: 'lastName',
        headerName: t('laLastName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
              disabled={isEndedEmployee(params.row)}
            />
          );
        },
        minWidth: 110,
      },
      {
        field: 'employeeNumber',
        headerName: t('laEmpNumber'),
        minWidth: 130,
      },
      {
        field: 'unitName',
        headerName: t('laOrgUnit'),
        minWidth: 180,
      },
      {
        field: 'unitNumber',
        headerName: t('laUnitNumber'),
        minWidth: 110,
      },
      {
        field: 'legalEntityName',
        headerName: t('laLegalEntity'),
        minWidth: 210,
      },
      {
        field: 'costCenterName',
        headerName: t('laCostCenter'),
        minWidth: 210,
      },
      {
        field: 'managerName',
        headerName: t('laManager'),
        width: 150,
      },
      {
        field: 'managerNumber',
        headerName: t('laManagerNumber'),
        width: 150,
      },
      {
        field: 'position',
        headerName: t('laPosition'),
        type: 'singleSelect',
        minWidth: 150,
        valueOptions: getSingleSelectColumnEnumValueOptions(
          positionEnums,
          positionOptions,
        ),
      },
      {
        field: 'jobTitle',
        headerName: t('laJobTitle'),
        width: 100,
      },
      {
        field: 'startDate',
        headerName: t('laStartDate'),
        type: 'date',
        valueFormatter: dateCellValueFormatter,
        minWidth: 130,
      },
      {
        field: 'arrivalDate',
        headerName: t('laArrivalDate'),
        type: 'date',
        valueFormatter: dateCellValueFormatter,
        width: 130,
      },
      {
        field: 'endDate',
        headerName: t('laEndDate'),
        type: 'date',
        valueFormatter: dateCellValueFormatter,
        width: 130,
      },
      {
        field: 'terminationDate',
        headerName: t('laTerminationDate'),
        type: 'date',
        valueFormatter: dateCellValueFormatter,
        width: 130,
      },
      {
        field: 'terminationReason',
        headerName: t('laTerminationReason'),
        type: 'singleSelect',
        width: 150,
        valueOptions: getSingleSelectColumnEnumValueOptions(
          terminationReasonEnums,
          terminationReasonOptions,
        ),
      },
      {
        field: 'trialPeriod',
        headerName: t('laTrialPeriod'),
        width: 120,
      },
    ];
  }, [
    t,
    goToEmployeeBasicDataView,
    isAccessToEndedEmployeeRestrictedByEmploymentStatus,
    dataColumnOptions,
    enums,
  ]);

  return {
    rows,
    columns,
  };
};
